import classes from './Input.module.scss'
import clsx from 'clsx'
import {
    forwardRef,
    ChangeEvent,
    FocusEvent,
    WheelEvent,
    InputHTMLAttributes,
} from 'react'

export interface InputProps
    extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'onBlur'> {
    value?: string | number | undefined
    onChange?: (value: string) => void
    onBlur?: (value: string) => void
    placeholder?: string
    className?: string
    innerClassName?: string
    type?: 'text' | 'email' | 'number' | 'password'
    error?: boolean | string
    disabled?: boolean
    shouldShowClearButton?: boolean
    maxLength?: number
    id?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
    (
        {
            value,
            onChange,
            onBlur,
            placeholder = '',
            className = '',
            innerClassName = '',
            type = 'text',
            error = false,
            disabled = false,
            shouldShowClearButton = true,
            id,
            maxLength,
            ...props
        },
        ref
    ) => {
        const handleClear = () => onChange?.('')

        const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
            onChange?.(e.target.value)
        }

        const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
            if (onBlur) {
                onBlur(e.target.value)
            }
        }

        const handleWheel = (e: WheelEvent<HTMLInputElement>) => {
            if (type === 'number') {
                e.currentTarget.blur()
            }
        }

        return (
            <div
                className={clsx(classes.input, className, {
                    [classes.error]: error,
                })}
            >
                <input
                    ref={ref}
                    id={id}
                    type={type}
                    value={value}
                    onChange={handleChange}
                    placeholder={placeholder}
                    className={clsx(
                        classes.field,
                        {
                            [classes.disabled]: disabled,
                            [classes.fieldError]: error,
                        },
                        innerClassName
                    )}
                    disabled={disabled}
                    onBlur={handleBlur}
                    onWheel={handleWheel}
                    maxLength={maxLength}
                    {...props}
                />
                {!!value && shouldShowClearButton && (
                    <button
                        type="button"
                        onClick={handleClear}
                        className={classes.clearButton}
                        aria-label="Clear input"
                    >
                        ×
                    </button>
                )}
            </div>
        )
    }
)

Input.displayName = 'Input'
